// Reunderwriting Module (Tier 3) PR 3 — typed API client for the case
// CRUD/list + per-case child surfaces (checklist / assessments / outcome /
// failures / assignments / approval) shipped by the PR 3 backend controllers
// in src/PhoenixCapital.Api/Controllers/Reunderwriting/.

import { api } from "./client";
import type { PagedResult } from "@/types/api";
import type {
  ArchiveReunderwritingCaseRequest,
  CriteriaLane,
  DeclineReunderwritingCaseRequest,
  IssueReunderwritingLaneAssessmentRequest,
  PagedReunderwritingEscalations,
  PagedReunderwritingFailures,
  ResolveReunderwritingEscalationRequest,
  ReunderwritingApprovalRequest,
  ReunderwritingApprovalResult,
  ReunderwritingAssignmentDto,
  ReunderwritingCaseDto,
  ReunderwritingCaseListItemDto,
  ReunderwritingCaseListQuery,
  ReunderwritingChecklistItemDto,
  ReunderwritingEscalationDetailDto,
  ReunderwritingEscalationFilter,
  ReunderwritingEscalationRecordDto,
  ReunderwritingEventTypeLookupDto,
  ReunderwritingFailureDto,
  ReunderwritingFailureFilter,
  ReunderwritingFailuresMetricsResponse,
  ReunderwritingLaneAssessmentDto,
  ReunderwritingMetricsFilter,
  ReunderwritingOutcomeDto,
  ReunderwritingPipelineMetricsResponse,
  ReunderwritingQualityMetricsResponse,
  ReunderwritingTimeToCompletionResponse,
  UpdateReunderwritingFailureRequest,
  UpdateReunderwritingJustificationRequest,
  UpsertReunderwritingAssignmentRequest,
  WithdrawReunderwritingCaseRequest,
} from "@/types/reunderwriting";

export const reunderwritingCasesApi = {
  list: (query?: ReunderwritingCaseListQuery) =>
    api.get<PagedResult<ReunderwritingCaseListItemDto>>(
      "/reunderwriting/cases",
      query as Record<string, unknown> | undefined,
    ),

  get: (id: string) =>
    api.get<ReunderwritingCaseDto>(`/reunderwriting/cases/${id}`),

  launch: (loanId: string, body: {
    eventTypeCode: string;
    subjectCounterpartyKind: "Person" | "Entity" | "Business";
    subjectPersonId?: string;
    subjectEntityId?: string;
    subjectBusinessId?: string;
    launchReason: string;
  }) => api.post<ReunderwritingCaseDto>(`/loans/${loanId}/reunderwrite`, body),

  decline: (id: string, body: DeclineReunderwritingCaseRequest) =>
    api.post<ReunderwritingCaseDto>(`/reunderwriting/cases/${id}/decline`, body),

  withdraw: (id: string, body: WithdrawReunderwritingCaseRequest) =>
    api.post<ReunderwritingCaseDto>(`/reunderwriting/cases/${id}/withdraw`, body),

  archive: (id: string, body: ArchiveReunderwritingCaseRequest) =>
    api.post<ReunderwritingCaseDto>(`/reunderwriting/cases/${id}/archive`, body),

  restore: (id: string) =>
    api.post<ReunderwritingCaseDto>(`/reunderwriting/cases/${id}/restore`),

  advanceToClosing: (id: string) =>
    api.post<ReunderwritingCaseDto>(`/reunderwriting/cases/${id}/advance-to-closing`),

  approve: (id: string, body: ReunderwritingApprovalRequest) =>
    api.post<ReunderwritingApprovalResult>(`/reunderwriting/cases/${id}/approve`, body),
};

export const reunderwritingChecklistApi = {
  listByLane: (caseId: string, lane: CriteriaLane) =>
    api.get<ReunderwritingChecklistItemDto[]>(
      `/reunderwriting/cases/${caseId}/checklist`,
      { lane },
    ),

  updateJustification: (
    caseId: string,
    itemId: string,
    body: UpdateReunderwritingJustificationRequest,
  ) =>
    api.put<ReunderwritingChecklistItemDto>(
      `/reunderwriting/cases/${caseId}/checklist/${itemId}`,
      body,
    ),

  certify: (caseId: string, itemId: string) =>
    api.post<ReunderwritingChecklistItemDto>(
      `/reunderwriting/cases/${caseId}/checklist/${itemId}/certify`,
    ),

  escalate: (caseId: string, itemId: string, escalationReason: string) =>
    api.post<ReunderwritingChecklistItemDto>(
      `/reunderwriting/cases/${caseId}/checklist/${itemId}/escalate`,
      { escalationReason },
    ),

  decline: (caseId: string, itemId: string, failureReason: string) =>
    api.post<ReunderwritingChecklistItemDto>(
      `/reunderwriting/cases/${caseId}/checklist/${itemId}/decline`,
      { failureReason },
    ),

  waive: (caseId: string, itemId: string, rationale: string) =>
    api.post<ReunderwritingChecklistItemDto>(
      `/reunderwriting/cases/${caseId}/checklist/${itemId}/waive`,
      { rationale },
    ),
};

export const reunderwritingAssessmentsApi = {
  list: (caseId: string) =>
    api.get<ReunderwritingLaneAssessmentDto[]>(
      `/reunderwriting/cases/${caseId}/assessments`,
    ),

  get: (caseId: string, lane: CriteriaLane) =>
    api.get<ReunderwritingLaneAssessmentDto>(
      `/reunderwriting/cases/${caseId}/assessments/${lane}`,
    ),

  issue: (
    caseId: string,
    lane: CriteriaLane,
    body: IssueReunderwritingLaneAssessmentRequest,
  ) =>
    api.post<ReunderwritingLaneAssessmentDto>(
      `/reunderwriting/cases/${caseId}/assessments/${lane}/issue`,
      body,
    ),

  outcome: (caseId: string) =>
    api.get<ReunderwritingOutcomeDto>(`/reunderwriting/cases/${caseId}/outcome`),

  finalize: (caseId: string) =>
    api.post<ReunderwritingOutcomeDto>(
      `/reunderwriting/cases/${caseId}/outcome/finalize`,
    ),
};

export const reunderwritingAssignmentsApi = {
  list: (caseId: string) =>
    api.get<ReunderwritingAssignmentDto[]>(
      `/reunderwriting/cases/${caseId}/assignments`,
    ),

  upsert: (
    caseId: string,
    roleCode: string,
    body: UpsertReunderwritingAssignmentRequest,
  ) =>
    api.put<ReunderwritingAssignmentDto>(
      `/reunderwriting/cases/${caseId}/assignments/${roleCode}`,
      body,
    ),

  markNa: (caseId: string, roleCode: string) =>
    api.post<ReunderwritingAssignmentDto>(
      `/reunderwriting/cases/${caseId}/assignments/${roleCode}/n-a`,
    ),
};

export const reunderwritingFailuresApi = {
  listByCase: (caseId: string) =>
    api.get<ReunderwritingFailureDto[]>(
      `/reunderwriting/cases/${caseId}/failures`,
    ),

  get: (id: string) =>
    api.get<ReunderwritingFailureDto>(`/reunderwriting/failures/${id}`),

  markReviewed: (id: string, notes: string | undefined) =>
    api.post<ReunderwritingFailureDto>(`/reunderwriting/failures/${id}/review`, {
      notes,
    }),

  resolve: (id: string, body: { certifiedResolved: boolean; resolutionNotes: string }) =>
    api.post<ReunderwritingFailureDto>(
      `/reunderwriting/failures/${id}/resolve`,
      body,
    ),
};

export const reunderwritingLookupsApi = {
  eventTypes: () =>
    api.get<ReunderwritingEventTypeLookupDto[]>(
      "/reunderwriting/lookups/event-types",
    ),
};

// ---- PR 4: page-level Failures, Escalations, and Metrics clients ----

function buildExportQs(query: Record<string, unknown> | undefined): string {
  const params = new URLSearchParams();
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "" && k !== "page" && k !== "pageSize") {
        params.append(k, String(v));
      }
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export const reunderwritingFailureTrackingApi = {
  list: (query?: ReunderwritingFailureFilter) =>
    api.get<PagedReunderwritingFailures>(
      "/reunderwriting/failures",
      query as Record<string, unknown> | undefined,
    ),
  update: (id: string, body: UpdateReunderwritingFailureRequest) =>
    api.patch<ReunderwritingFailureDto>(`/reunderwriting/failures/${id}`, body),
  exportCsvUrl: (query?: ReunderwritingFailureFilter) =>
    `/api/reunderwriting/failures/export.csv${buildExportQs(query as Record<string, unknown> | undefined)}`,
};

export const reunderwritingEscalationsApi = {
  list: (query?: ReunderwritingEscalationFilter) =>
    api.get<PagedReunderwritingEscalations>(
      "/reunderwriting/escalations",
      query as Record<string, unknown> | undefined,
    ),
  get: (id: string) =>
    api.get<ReunderwritingEscalationDetailDto>(`/reunderwriting/escalations/${id}`),
  getMostRecentForItem: (checklistItemId: string) =>
    api.get<ReunderwritingEscalationRecordDto | null>(
      `/reunderwriting/escalations/by-item/${checklistItemId}`,
    ),
  approve: (id: string, body: ResolveReunderwritingEscalationRequest) =>
    api.post<ReunderwritingEscalationRecordDto>(
      `/reunderwriting/escalations/${id}/approve`,
      body,
    ),
  deny: (id: string, body: ResolveReunderwritingEscalationRequest) =>
    api.post<ReunderwritingEscalationRecordDto>(
      `/reunderwriting/escalations/${id}/deny`,
      body,
    ),
  exportCsvUrl: (query?: ReunderwritingEscalationFilter) =>
    `/api/reunderwriting/escalations/export.csv${buildExportQs(query as Record<string, unknown> | undefined)}`,
};

export const reunderwritingMetricsApi = {
  pipeline: (q?: ReunderwritingMetricsFilter) =>
    api.get<ReunderwritingPipelineMetricsResponse>(
      "/reunderwriting/metrics/pipeline",
      q as Record<string, unknown> | undefined,
    ),
  quality: (q?: ReunderwritingMetricsFilter) =>
    api.get<ReunderwritingQualityMetricsResponse>(
      "/reunderwriting/metrics/quality",
      q as Record<string, unknown> | undefined,
    ),
  failures: (q?: ReunderwritingMetricsFilter) =>
    api.get<ReunderwritingFailuresMetricsResponse>(
      "/reunderwriting/metrics/failures",
      q as Record<string, unknown> | undefined,
    ),
  timeToCompletion: (q?: ReunderwritingMetricsFilter) =>
    api.get<ReunderwritingTimeToCompletionResponse>(
      "/reunderwriting/metrics/time-to-completion",
      q as Record<string, unknown> | undefined,
    ),
};
